import React, { useEffect, useRef } from 'react';
import { Maximize2, ExternalLink } from 'lucide-react';
import { useOpenInNebula } from "../../hooks/useOpenInNebula";
import { useTheme } from "../../context/ThemeProvider";
import { getColorSchemePalette } from "../../context/colorSchemes";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import useDownloadFile from "../../hooks/useDownloadFile";
import VizCopyButton from "../shared/Viz/VizCopyButton";
import VizDownloadButton from "../shared/Viz/VizDownloadButton";
import useNativeFullscreen from "../../hooks/useNativeFullscreen";

// Virtual iframe dimensions (rendered at full size, then scaled down into a card)
const IFRAME_W = 1440;
const IFRAME_H = 900;
const PREVIEW_W = 360;  // card width — two fit side by side in chat
const PREVIEW_H = 220;  // half-height portrait card
const SCALE = PREVIEW_W / IFRAME_W; // ≈ 0.25

const HtmlViz = ({ code, toggleElement = null }) => {
  const iframeRef = useRef(null);
  const { ref: containerRef, isFullscreen, enterFullscreen } = useNativeFullscreen();
  const { copied, copyToClipboard } = useCopyToClipboard({ caller: "HtmlViz" });
  const { downloadFile } = useDownloadFile({ caller: "HtmlViz" });
  const { theme, colorScheme } = useTheme();
  const palette = getColorSchemePalette(colorScheme, theme);
  const openInNebula = useOpenInNebula();

  const handleCopy = () => copyToClipboard(code);
  const handleDownload = () => downloadFile(code, `page-${Date.now()}.html`, "text/html");

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    // Debounce by 500ms so the iframe doesn't thrash on every streaming chunk.
    // The cleanup revokes any pending blob URL to avoid memory leaks.
    let url;
    const timer = setTimeout(() => {
      // Inject CSS variables and body defaults so LLM-generated HTML that uses
      // var(--photon-*) or relies on default body colors renders correctly in both modes.
      const themeStyle = `<style>
        :root {
          color-scheme: ${theme};
          --photon-bg: ${palette.background};
          --photon-surface: ${palette.sidebar};
          --photon-text: ${palette.foreground};
          --photon-text-muted: ${palette.mutedForeground};
          --photon-border: ${palette.border};
          --photon-grid: ${palette.border};
          --photon-primary: ${palette.primary};
        }
        body { background-color: var(--photon-bg); color: var(--photon-text); }
      </style>`;
      const themedCode = code.includes('<head>')
        ? code.replace('<head>', `<head>${themeStyle}`)
        : themeStyle + code;
      const blob = new Blob([themedCode], { type: 'text/html' });
      url = URL.createObjectURL(blob);
      iframe.src = url;
    }, 500);
    return () => {
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [code, palette, theme]);

  return (
    <div
      ref={containerRef}
      className={`inline-block my-4 rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 overflow-hidden bg-fill-white dark:bg-fill-canvas-page-surface group cursor-pointer hover:shadow-lg transition-shadow ${isFullscreen ? "w-screen h-screen flex flex-col rounded-none border-0" : ""}`}
      style={isFullscreen ? {} : { width: `${PREVIEW_W}px` }}
      onClick={!isFullscreen ? enterFullscreen : undefined}
    >
      {/* Iframe — scaled preview normally, full size in fullscreen */}
      <div
        style={isFullscreen ? { flex: 1, overflow: 'hidden', position: 'relative' } : {
          width: `${PREVIEW_W}px`,
          height: `${PREVIEW_H}px`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <iframe
          ref={iframeRef}
          title="HTML Visualization"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          allowFullScreen
          style={isFullscreen ? {
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          } : {
            width: `${IFRAME_W}px`,
            height: `${IFRAME_H}px`,
            transform: `scale(${SCALE})`,
            transformOrigin: 'top left',
            border: 'none',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Footer bar — hidden in fullscreen */}
      {!isFullscreen && (
        <div className="px-3 py-1.5 border-t border-edge-gray-200 dark:border-edge-gray-700 flex justify-between items-center bg-fill-gray-50 dark:bg-fill-hex-252525">
          {toggleElement ?? <span className="text-[10px] font-mono text-ink-gray-400 dark:text-ink-gray-500 truncate">HTML Preview</span>}
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <VizCopyButton copied={copied} onClick={handleCopy} tooltip="Copy HTML" className="!p-1" />
            <VizDownloadButton onClick={handleDownload} tooltip="Download .html" className="!p-1" />
            <button
              onClick={() => openInNebula()}
              className="flex items-center p-1 text-ink-gray-400 dark:text-ink-gray-500 hover:text-ink-gray-700 dark:hover:text-ink-gray-200 transition-colors"
              aria-label="Open in Nebula"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={enterFullscreen}
              className="flex items-center p-1 text-ink-gray-400 dark:text-ink-gray-500 hover:text-ink-gray-700 dark:hover:text-ink-gray-200 transition-colors"
              aria-label="Open fullscreen"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HtmlViz;
